import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { FileUpload } from "../Shared/FileUpload";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import { addPendingIncidentAttachment, addIncidentAttachmentToExisting, deleteIncidentAttachment } from "requests/customerService";
import { CUSTOMER_SERVICE_INCIDENT_ADD, CUSTOMER_SERVICE_INCIDENT_UPDATE, CUSTOMER_SERVICE_INCIDENT_DELETE, CUSTOMER_SERVICE_EMAIL_SEND } from "typeConstants/rights";
import { dateTimeToString } from "components/Shared/Formatters";
import WebConfig from "middlewares/WebConfig";
import { userHasRight } from "selectors/rights";
import { CustomerServiceIncidentAttachmentMetadata } from "interfaces/Mueller.Business.Models";

interface Props {
  incidentId?: number;
  attachments: CustomerServiceIncidentAttachmentMetadata[];
  onAttachmentsChanged: (nextAttachments: CustomerServiceIncidentAttachmentMetadata[]) => void;
  disabled?: boolean;
  viewOnly?: boolean;

  // redux
  token: string;
  canAdd: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

const IncidentAttachments = ({
  incidentId,
  attachments,
  onAttachmentsChanged,
  disabled,
  token,
  canAdd,
  canUpdate,
  canDelete,
  viewOnly,
} : Props) => {
  const mounted = useRef<boolean>(true);

  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [downloadLookupKey, setDownloadLookupKey] = useState<string>(null);

  useEffect(() => {
    return () => { mounted.current = false; };
  }, []);

  const onLoadStart = () => {
    setIsUploading(true);
  };

  const onLoad = (file: File, result: ArrayBuffer) => {
    const successCallback = (data: CustomerServiceIncidentAttachmentMetadata) => {
      const nextAttachments = attachments.slice(0);
      nextAttachments.push(data);
      onAttachmentsChanged(nextAttachments);
      setIsUploading(false);
    };
    const errorCallback = () => {
      setIsUploading(false);
    };

    if (incidentId) {
      addIncidentAttachmentToExisting(file.name, incidentId, result, successCallback, errorCallback, mounted);
    }
    else {
      addPendingIncidentAttachment(file.name, result, successCallback, errorCallback, mounted);
    }
  };

  const downloadAttachment = (lookupKey: string, fileDisplayName: string) => {
    if (downloadLookupKey !== null) return;

    setDownloadLookupKey(lookupKey);

    // using a regular download anchor for this will trigger the 'window.onbeforeunload' event registered in MCaseView, so we have to do some gymnastics
    fetch(`${WebConfig.webApiRoot}/customer-service/incident/attachments/${lookupKey}`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`,
      },
      credentials: "include",
    })
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileDisplayName; // note: if the filename doesn't have a file extension, the browser automatically appends '.htm'. This is unfortunate and solved with the regular anchor, but we can't use that without triggering the navigation event!
        document.body.appendChild(a);
        a.click();
        a.remove();
        setDownloadLookupKey(null);
      });
  };

  const deleteAttachment = (lookupKey: string) => {
    const nextAttachments = attachments.slice(0);
    const remove = nextAttachments.findIndex(item => item.lookupKey === lookupKey);
    nextAttachments.splice(remove, 1);
    onAttachmentsChanged(nextAttachments);

    deleteIncidentAttachment(lookupKey, () => {}, () => {}, mounted);
  };

  return (
    <div>
      <div style={{display:"flex", justifyContent:"space-between"}}>
        <h3>Attachments</h3>
        {(canAdd || canUpdate) && !viewOnly &&
          <FileUpload 
            id="incidentFileUpload"
            allowDuplicates
            disabled={isUploading || disabled}
            label="Select or Drop File Here"
            onLoadStart={onLoadStart}
            onLoad={onLoad}
            readAs={"ArrayBuffer"}
            maxSize={20000000}
            onChange={() => {}} // grr why do I need to define this
            onSizeError={() => { alert("Error: Max file size is 20MB"); }}
          />
        }
      </div>      
      {attachments?.length > 0 && 
        <TableContainer>
          <Table>
            <TableBody>
              { attachments.map(({ lookupKey, fileDisplayName, createDate }) => (
                <TableRow key={lookupKey}>
                  <TableCell sx={{ flexGrow: 1 }}>{fileDisplayName}</TableCell>
                  <TableCell>{ dateTimeToString(createDate) }</TableCell>
                  <TableCell>
                    {downloadLookupKey !== lookupKey &&
                      <i className="md-icon material-icons" style={{cursor:"pointer"}} onClick={() => { downloadAttachment(lookupKey, fileDisplayName); }}>download</i>
                    }
                    {downloadLookupKey === lookupKey &&
                      <CircularProgress 
                        size={20}
                      />
                    }
                  </TableCell>
                  {canDelete && !viewOnly &&
                    <TableCell>
                      <i className="md-icon material-icons" style={{cursor:"pointer"}} onClick={() => { deleteAttachment(lookupKey); }}>delete_forever</i>
                    </TableCell>
                  }
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      }      
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    token: state.user.profile.token,
    canAdd: userHasRight(CUSTOMER_SERVICE_INCIDENT_ADD)(state) || userHasRight(CUSTOMER_SERVICE_EMAIL_SEND)(state),
    canUpdate: userHasRight(CUSTOMER_SERVICE_INCIDENT_UPDATE)(state),
    canDelete: userHasRight(CUSTOMER_SERVICE_INCIDENT_DELETE)(state) || userHasRight(CUSTOMER_SERVICE_EMAIL_SEND)(state),
  };
};

export default connect(mapStateToProps, null)(IncidentAttachments);